import type { ExpressionSpecification, FilterSpecification } from 'maplibre-gl'
import { EROSION_CONTEXT_COLORS, ROAD_SOURCE_BY_SITE, VisibleLayers } from '~/constants'
import { useAppStore, usePMTiles } from '~/hooks'
import { Layer, Source } from 'react-map-gl/maplibre'

/** Default site zoom — keep today's pixel widths here. */
const SITE_ZOOM = 12.75

function zoomDependentRoadWidth(widthAtSiteZoom: number): ExpressionSpecification {
  return [
    'interpolate',
    ['exponential', 1.5],
    ['zoom'],
    8,
    widthAtSiteZoom * 0.15,
    SITE_ZOOM,
    widthAtSiteZoom,
  ]
}

export function RoadLayer() {
  const { sources, layerInfo } = usePMTiles()
  const { layerVisibility, DEM, returnPeriod, adaptation, dataset, cosmosSite } = useAppStore()
  const source = ROAD_SOURCE_BY_SITE[cosmosSite]
  const url = sources[source]
  const sourceLayer = layerInfo?.[source]?.[0]?.layer
  const isErosion = dataset === 'erosion'
  const SCENARIO_KEY = `dem${DEM}_rp${returnPeriod}_adapt${adaptation}`

  if (!url || !sourceLayer) return null

  return (
    <Source id={source} key={source} type="vector" url={`pmtiles://${url}`}>
      <Layer
        id={source}
        source={source}
        source-layer={sourceLayer}
        type="line"
        layout={{
          visibility:
            (dataset === 'cosmos' || isErosion) && layerVisibility[VisibleLayers.ROAD]
              ? 'visible'
              : 'none',
        }}
        paint={
          isErosion
            ? {
                'line-color': EROSION_CONTEXT_COLORS.road,
                'line-width': zoomDependentRoadWidth(1.5),
                'line-opacity': 0.7,
              }
            : {
                'line-color': 'purple',
                'line-width': zoomDependentRoadWidth(2),
              }
        }
        // Roads are stored once per return period / year / adaptation. A `true`
        // filter paints every copy at once and overdraws the same segment.
        filter={['==', ['get', 'scenario'], SCENARIO_KEY] as FilterSpecification}
      />
    </Source>
  )
}
